import { defineStore } from 'pinia';
import { addDoc, collection, deleteDoc, doc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { hasClientPortalPermission } from '~/lib/clientPortalPermissions';
import { waitForResumableUpload } from '~/lib/resumableUpload';
import { toMillis } from '~/lib/timestamps';
import type { UploadedFile } from '~/types/models';
import { privateImmutableUploadMetadata } from '~/lib/storageUploadMetadata';
import { buildClientPortalDataScopeKey } from '~/lib/clientPortalDataScope';

type FileEntityType = UploadedFile['entityType'];

function getFetchStatusCode(error: unknown): number | null {
    if (!error || typeof error !== 'object') {
        return null;
    }

    const fetchError = error as {
        status?: number;
        statusCode?: number;
        response?: { status?: number };
        data?: { statusCode?: number };
    };

    return fetchError.statusCode
        ?? fetchError.status
        ?? fetchError.response?.status
        ?? fetchError.data?.statusCode
        ?? null;
}

function isSkippablePortalFilesError(error: unknown): boolean {
    const statusCode = getFetchStatusCode(error);
    return statusCode === 403 || statusCode === 404;
}

export const useClientPortalFilesStore = defineStore('clientPortalFiles', {
    state: () => ({
        files: [] as UploadedFile[],
        filesByEntity: {} as Record<string, UploadedFile[]>,
        listScopeKey: null as string | null,
        loading: false,
        uploading: false,
        error: null as string | null,
    }),

    actions: {
        _entityKey(entityType: FileEntityType, entityId: string): string {
            return `${entityType}:${entityId}`;
        },

        scopedEntityKey(tenantId: string, entityType: FileEntityType, entityId: string): string {
            const portalStore = useClientPortalStore();
            return `${buildClientPortalDataScopeKey({
                tenantId,
                clientId: portalStore.currentClient?.id,
                previewClientId: portalStore.previewingAsClientId,
            })}|${this._entityKey(entityType, entityId)}`;
        },

        async _fetchEntityFiles(tenantId: string, entityType: FileEntityType, entityId: string): Promise<UploadedFile[]> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const clientId = portalStore.currentClient?.id;
            const token = await $auth.currentUser?.getIdToken();

            if (!hasClientPortalPermission(portalStore.currentAccess, 'viewFiles')) {
                return [];
            }

            if (!clientId || !token) {
                throw new Error('You must be signed in to load client portal files.');
            }

            const response = await $fetch<{ files: UploadedFile[] }>('/api/client-portal/files', {
                query: {
                    tenantId,
                    clientId,
                    entityType,
                    entityId,
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            return Array.isArray(response.files) ? response.files : [];
        },

        _sortFiles(items: UploadedFile[]): UploadedFile[] {
            return [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        },

        sanitizeFilename(filename: string): string {
            return filename.replace(/[^a-zA-Z0-9.-]/g, '_');
        },

        async fetchForEntity(tenantId: string, entityType: FileEntityType, entityId: string): Promise<UploadedFile[]> {
            this.loading = true;
            this.error = null;
            const scopedEntityKey = this.scopedEntityKey(tenantId, entityType, entityId);

            try {
                const files = this._sortFiles(await this._fetchEntityFiles(tenantId, entityType, entityId));
                this.filesByEntity[scopedEntityKey] = files;
                return files;
            } catch (error) {
                this.error = `Error loading files: ${error}`;
                console.error(this.error);
                this.filesByEntity[scopedEntityKey] = [];
                return [];
            } finally {
                this.loading = false;
            }
        },

        async uploadForEntity(params: {
            tenantId: string;
            entityType: FileEntityType;
            entityId: string;
            file: File;
            onProgress?: (progress: number) => void;
        }): Promise<string> {
            const { $db, $storage, $auth } = useNuxtApp();
            const tenantStore = useTenantStore();
            const portalStore = useClientPortalStore();
            const isPortalClientSession = Boolean(portalStore.currentClient?.id);
            const scopedEntityKey = this.scopedEntityKey(params.tenantId, params.entityType, params.entityId);
            const listScopeKey = this.listScopeKey;
            this.uploading = true;
            this.error = null;

            try {
                if (isPortalClientSession && !hasClientPortalPermission(portalStore.currentAccess, 'uploadFiles')) {
                    throw new Error('You do not have permission to upload files.');
                }

                const authUser = $auth.currentUser;
                if (!authUser?.uid) {
                    throw new Error('You must be logged in to upload files.');
                }

                const cleanFilename = this.sanitizeFilename(params.file.name);
                const storagePath = `tenants/${params.tenantId}/files/${params.entityType}/${params.entityId}/${Date.now()}-${cleanFilename}`;
                const fileRef = storageRef($storage, storagePath);

                if (!isPortalClientSession) {
                    await tenantStore.ensureStorageCapacity(params.tenantId, params.file.size);
                }
                const uploadTask = uploadBytesResumable(fileRef, params.file, privateImmutableUploadMetadata(params.file));
                await waitForResumableUpload(uploadTask, { onProgress: params.onProgress });
                const fileURL = await getDownloadURL(fileRef);

                const payload = {
                    tenantId: params.tenantId,
                    entityType: params.entityType,
                    entityId: params.entityId,
                    entityKey: this._entityKey(params.entityType, params.entityId),
                    fileName: params.file.name,
                    fileURL,
                    storagePath,
                    contentType: params.file.type || 'application/octet-stream',
                    sizeBytes: params.file.size,
                    uploadedBy: authUser.uid,
                    uploadedByName: authUser.displayName || authUser.email || 'Client',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                };

                const fileDocRef = await addDoc(collection($db, `tenants/${params.tenantId}/files`), payload);
                const created = { id: fileDocRef.id, ...payload } as UploadedFile;
                this.filesByEntity[scopedEntityKey] = this._sortFiles([
                    created,
                    ...(this.filesByEntity[scopedEntityKey] || []),
                ]);
                if (listScopeKey && this.listScopeKey === listScopeKey) {
                    this.files = this._sortFiles([created, ...this.files]);
                }
                if (!isPortalClientSession) {
                    await tenantStore.adjustStorageUsage(params.tenantId, params.file.size);
                }
                return fileDocRef.id;
            } catch (error) {
                this.error = `Error uploading file: ${error}`;
                console.error(this.error);
                throw error;
            } finally {
                this.uploading = false;
            }
        },

        async fetchForClient(tenantId: string, clientId: string, projectIds: string[] = []): Promise<void> {
            const portalStore = useClientPortalStore();
            const scopeKey = buildClientPortalDataScopeKey({ tenantId, clientId, previewClientId: portalStore.previewingAsClientId });
            if (this.listScopeKey !== scopeKey) {
                this.files = [];
                this.listScopeKey = scopeKey;
            }
            this.loading = true;
            this.error = null;

            try {
                if (!hasClientPortalPermission(portalStore.currentAccess, 'viewFiles')) {
                    this.files = [];
                    return;
                }

                const shouldFetchClientFiles = portalStore.currentAccess?.projectAccessMode !== 'selected';
                const scopedFiles = await Promise.allSettled([
                    ...(shouldFetchClientFiles ? [this._fetchEntityFiles(tenantId, 'client', clientId)] : []),
                    ...projectIds.map(projectId => this._fetchEntityFiles(tenantId, 'project', projectId)),
                ]);

                const failedResult = scopedFiles.find((result) => result.status === 'rejected' && !isSkippablePortalFilesError(result.reason));
                if (failedResult?.status === 'rejected') {
                    throw failedResult.reason;
                }

                if (this.listScopeKey === scopeKey) {
                    this.files = this._sortFiles(scopedFiles.flatMap((result) => (
                        result.status === 'fulfilled' ? result.value : []
                    )));
                }
            } catch (error) {
                if (this.listScopeKey !== scopeKey) return;
                this.error = `Error loading files: ${error}`;
                console.error(this.error);
                this.files = [];
            } finally {
                if (this.listScopeKey === scopeKey) this.loading = false;
            }
        },

        async uploadForClient(params: {
            tenantId: string;
            clientId: string;
            file: File;
        }): Promise<void> {
            await this.uploadForEntity({
                tenantId: params.tenantId,
                entityType: 'client',
                entityId: params.clientId,
                file: params.file,
            });
        },

        async deleteForClient(tenantId: string, file: UploadedFile): Promise<void> {
            const { $db, $storage } = useNuxtApp();
            const tenantStore = useTenantStore();
            const portalStore = useClientPortalStore();
            const isPortalClientSession = Boolean(portalStore.currentClient?.id);
            const scopedEntityKey = this.scopedEntityKey(tenantId, file.entityType, file.entityId);
            const listScopeKey = this.listScopeKey;
            this.error = null;

            if (isPortalClientSession && !hasClientPortalPermission(portalStore.currentAccess, 'uploadFiles')) {
                throw new Error('You do not have permission to remove files.');
            }

            await deleteDoc(doc($db, `tenants/${tenantId}/files`, file.id));

            let storageDeleted = !file.storagePath;
            if (file.storagePath) {
                try {
                    await deleteObject(storageRef($storage, file.storagePath));
                } catch (storageError) {
                    storageDeleted = false;
                    console.warn('Failed to delete storage object:', storageError);
                }
            }

            if (listScopeKey && this.listScopeKey === listScopeKey) {
                this.files = this.files.filter((entry) => entry.id !== file.id);
            }
            this.filesByEntity[scopedEntityKey] = (this.filesByEntity[scopedEntityKey] || [])
                .filter((entry) => entry.id !== file.id);

            if (!isPortalClientSession && storageDeleted && file.sizeBytes) {
                await tenantStore.adjustStorageUsage(tenantId, -Math.abs(Number(file.sizeBytes)));
            }
        },
    },
});
